// @ts-check

/**
 * @typedef {Object} PlaceholderTags
 * @property {string|undefined} displayName - Display name of the placeholder
 * @property {string|undefined} customName - Custom name of the placeholder
 * @property {string|undefined} customTooltip - Tooltip description for the placeholder
 * @property {string|undefined} color - Color associated with the placeholder
 */

/**
 * @typedef {Object} PlaceholderData
 * @property {string|undefined} _displayName - Display name of the placeholder
 * @property {Object.<string, *>|undefined} tags - Custom fields associated with the placeholder from BackOffice
 * @property {Object.<string, *>|undefined} parentPlaceholder - Detailed data from the placeholder
 */

/**
 * @typedef {Object} TextItem
 * @property {string} name - Name of the text item
 * @property {string} text - Content of the text item
 */

/**
 * @typedef {Object} ToggleChoice
 * @property {string} title - Display title of the toggle
 * @property {Object.<string, any>} value - Current state of the toggle
 */

/**
 * @typedef {Object.<string, ToggleChoice>} ToggleChoices
 * - Map of toggle names to their choice data
 */

/**
 * Class to extract form data from the editor
 */
class FormContent {
  /**
   * @type {null | any}
   */
  editor = null;

  /**
   * Creates a FormContent instance
   * @param {null | HTMLCanvasElement} editor - The canvas element acting as the editor
   */
  constructor(editor) {
    this.editor = editor;
  }

  /**
   * Retrieves placeholder tags from the editor
   * @returns {null | PlaceholderTags[]} - Array of placeholder tag objects or null if no data
   */
  getPlaceholdersTags() {
    if (!this.editor) return null;

    /** @type {null | PlaceholderData[]} */
    const placeholdersData = this.editor.getPlaceholdersContent();
    if (!placeholdersData || !placeholdersData.length) return null;

    const placeholdersTags = placeholdersData.map((placeholderItem) => {
      const tags = placeholderItem?.tags || {};
      /** @type {Object.<string, string | undefined>} */
      return {
        name: placeholderItem.parentPlaceholder?.name,
        displayName: placeholderItem._displayName,
        customName: tags['customName'],
        customTooltip: tags['customTooltip'],
        color: tags['color'],
      };
    });
    return /** @type {PlaceholderTags[]} */ (placeholdersTags);
  }

  /**
   * Retrieves text items from the editor
   * @returns {null | TextItem[]} - Array of text items or null if no data
   */
  getTextItems() {
    if (!this.editor) return null;

    /** @type {null | { name: string; text: string; visualizationPermissions: { noPrint: boolean; noShow: boolean; } }[]} */
    const textItemsFull = this.editor.getTextItems();
    if (!textItemsFull || !textItemsFull.length) return null;

    const textItems = textItemsFull
      .filter((textItem) => !textItem.visualizationPermissions?.noShow)
      .map((textItem) => ({
        name: textItem['name'],
        text: textItem['text'],
      }));
    return /** @type {TextItem[]} */ (textItems);
  }

  /**
   * Retrieves toggle choices from the editor
   * @returns {null | { name: string; title: string; value: Object.<string, any> }[]} - Array of toggles or null
   */
  getToggles() {
    if (!this.editor) return null;

    /** @type {null | ToggleChoices} */
    const togglesChoices = this.editor.getToggleChoices();
    if (!togglesChoices || !Object.keys(togglesChoices)?.length) return null;

    const toggles = Object.entries(togglesChoices).map(([name, { title, value }]) => ({
      name,
      title,
      value: value.color,
    }));

    return toggles;
  }
}